import { useId, type ReactNode } from 'react';
import { Slider } from '@voll-intelligence/ui-kit/ui/slider';
import {
  LAST_SLOT_MINUTES,
  SLOT_MINUTES,
  formatSlotTime,
  minutesOfTime,
  timeOfMinutes,
} from './rental_window.js';

/** Uma régua: o nome à esquerda, a hora à direita, o slider embaixo — a mesma
 *  anatomia das réguas de horário dos filtros (`TimeRangeSection`). */
function clampMinutes(
  minutes: number,
  bounds: { readonly from?: string; readonly to?: string },
): number {
  const floor = bounds.from === undefined ? 0 : minutesOfTime(bounds.from);
  const ceiling = bounds.to === undefined ? LAST_SLOT_MINUTES : minutesOfTime(bounds.to);
  return Math.min(Math.max(minutes, floor), ceiling);
}

/** Na folha do celular a régua é mais grossa e o pino maior, ao alcance do
 *  dedo (#5662): trilho de 6px em `secondary`, pino de 20px com borda `primary`. */
const SHEET_SLIDER =
  '[&_[data-slot=slider-track]]:h-1.5! [&_[data-slot=slider-track]]:bg-secondary! [&_[data-slot=slider-thumb]]:size-5! [&_[data-slot=slider-thumb]]:border-primary!';

function TimeSlider({
  label,
  value,
  min,
  bounds,
  locale,
  large,
  onChange,
}: {
  readonly label: string;
  /** `HH:mm`. */
  readonly value: string;
  /** O piso da régua em `HH:mm` (a retirada no dia do piso); ausente ⇒ 00:00. */
  readonly min: string | undefined;
  /** Onde o valor TRAVA, sem mudar a escala (#5662): a régua segue o dia
   *  inteiro, para o pino da devolução ficar alinhado à hora da retirada, e o
   *  arraste para além do limite para nele. */
  readonly bounds: { readonly from?: string; readonly to?: string };
  readonly locale: string;
  readonly large: boolean;
  readonly onChange: (time: string) => void;
}): ReactNode {
  const labelId = useId();
  const text = formatSlotTime(locale, value);
  return (
    <div className="grid min-w-0 flex-1 gap-2">
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className={large ? 'text-base font-medium' : 'text-ui'}>
          {label}
        </span>
        <span
          className={`${large ? 'text-base font-medium' : 'text-ui'} text-muted-foreground tabular-nums`}
        >
          {text}
        </span>
      </div>
      <div className="px-1.5 pt-1 pb-2">
        <Slider
          min={min === undefined ? 0 : minutesOfTime(min)}
          max={LAST_SLOT_MINUTES}
          {...(large ? { className: SHEET_SLIDER } : {})}
          step={SLOT_MINUTES}
          value={minutesOfTime(value)}
          aria-labelledby={labelId}
          getAriaValueText={(_formatted, minutes) => formatSlotTime(locale, timeOfMinutes(minutes))}
          onValueChange={(minutes: number) => {
            onChange(timeOfMinutes(clampMinutes(minutes, bounds)));
          }}
        />
      </div>
    </div>
  );
}

/**
 * As HORAS da locação dentro do painel do período — o desenho do Figma
 * (#4513): no popover, retirada e devolução lado a lado em cima do calendário,
 * separadas por um fio; na folha do celular, EMPILHADAS no rodapé (perto do
 * polegar, #5662). Antes eram dois seletores na fileira da barra, longe do
 * período, e a janela que o calendário resumia não era a que a busca levava
 * (I5). Os limites de cada régua são os do período (`timeBounds`): no mesmo
 * dia a devolução não desce abaixo de retirada + 30 min.
 */
export function RentalTimeSliders({
  pickup,
  dropoff,
  pickupMin,
  pickupMax,
  dropoffMin,
  copy,
  inFooter = false,
  onChange,
}: {
  readonly pickup: string;
  readonly dropoff: string;
  /** O piso da retirada no dia escolhido; ausente ⇒ o dia inteiro. */
  readonly pickupMin: string | undefined;
  /** O teto da retirada (23:00 num período de um dia); ausente ⇒ o dia inteiro. */
  readonly pickupMax: string | undefined;
  /** O piso da devolução (retirada + 30 min num período de um dia). */
  readonly dropoffMin: string | undefined;
  readonly copy: {
    readonly calendarLocale: string;
    readonly pickupTimeLabel: string;
    readonly dropoffTimeLabel: string;
  };
  /** No rodapé da folha, que já dá o recuo lateral: as réguas empilham, maiores,
   *  e só o fio até o resumo. */
  readonly inFooter?: boolean;
  readonly onChange: (times: { readonly pickup: string; readonly dropoff: string }) => void;
}): ReactNode {
  return (
    <div
      className={`flex border-b border-border pb-3 ${inFooter ? 'flex-col gap-3' : 'gap-4 px-2 pt-2'}`}
    >
      <TimeSlider
        label={copy.pickupTimeLabel}
        value={pickup}
        min={pickupMin}
        bounds={pickupMax === undefined ? {} : { to: pickupMax }}
        locale={copy.calendarLocale}
        large={inFooter}
        onChange={(next) => {
          onChange({ pickup: next, dropoff });
        }}
      />
      {!inFooter && <div aria-hidden="true" className="w-px self-stretch bg-border" />}
      <TimeSlider
        label={copy.dropoffTimeLabel}
        value={dropoff}
        min={undefined}
        bounds={dropoffMin === undefined ? {} : { from: dropoffMin }}
        locale={copy.calendarLocale}
        large={inFooter}
        onChange={(next) => {
          onChange({ pickup, dropoff: next });
        }}
      />
    </div>
  );
}
